import { useMutation } from '@apollo/client'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { IconButton, InputAdornment } from '@mui/material'
import Button from '@mui/material/Button'
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik'
import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import useToast from 'shared/components/Toast/useToast'
import { ResetPassword } from 'shared/graphql/auth.gql'
import { ResetPasswordFormValues } from 'shared/models/user'
import { commonRouteConfig } from 'shared/routeConfig'
import { setToken } from 'shared/utils/auth'
import * as Yup from 'yup'
import SignUpSignIn from '../index'
import { StyledButtonContainer, StyledTextInput } from '../styled'

const initialFormValues = {
    newPassword: '',
    confirmPassword: '',
}

const ResetPasswordForm: React.FC = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const toast = useToast()
    const [resetPassword] = useMutation(ResetPassword)

    const [showPassword, setShowPassword] = React.useState(false)
    const [showPasswordConfirmation, setShowPasswordConfirmation] =
        React.useState(false)

    const handleClickShowPassword = () => setShowPassword((show) => !show)
    const handleClickShowPasswordConfirmation = () =>
        setShowPasswordConfirmation((show) => !show)

    const handleMouseDownPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault()
    }

    const resetPasswordValidationSchema = Yup.object().shape({
        newPassword: Yup.string()
            .matches(
                /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/,
                'Minimum 8 characters, including one uppercase letter, one lowercase letter, and one number'
            )
            .required('Required'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('newPassword')], 'Passwords must match')
            .required('Required'),
    })

    const sessionToken = location.search.split('=')[1]

    useEffect(() => {
        if (!sessionToken) {
            navigate(commonRouteConfig.login.path)
        }
    }, [sessionToken, navigate])

    const handleSubmit = async (
        values: ResetPasswordFormValues,
        helpers: FormikHelpers<ResetPasswordFormValues>
    ): Promise<null> => {
        const { newPassword } = values
        try {
            const res = await resetPassword({
                variables: {
                    token: sessionToken,
                    newPassword: newPassword,
                },
            })

            if (
                res.data &&
                res.data.resetPassword &&
                res.data.resetPassword.token
            ) {
                setToken(res.data.resetPassword.token)
                navigate('/')
            } else if (
                res.data &&
                res.data.resetPassword &&
                res.data.resetPassword.token === null
            ) {
                console.error('ERROR: ', res.data.resetPassword.error)
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
            }
        } catch (error) {
            console.error('ERROR: ', error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
            })
        } finally {
            helpers.setSubmitting(false)
        }
        return null
    }

    return (
        <SignUpSignIn title="Reset Password">
            <Formik
                initialValues={initialFormValues}
                onSubmit={handleSubmit}
                validationSchema={resetPasswordValidationSchema}
                validateOnBlur
                style={{ width: '100%' }}
            >
                {({ isSubmitting, errors, touched }) => (
                    <Form>
                        <Field name="newPassword">
                            {({ field }: FieldProps<string>) => (
                                <StyledTextInput
                                    {...field}
                                    InputLabelProps={{ shrink: true }}
                                    id="newPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    error={
                                        !!errors.newPassword &&
                                        touched.newPassword
                                    }
                                    helperText={
                                        !!errors.newPassword &&
                                        touched.newPassword
                                            ? errors.newPassword
                                            : 'Minimum 8 characters, including one uppercase letter, one lowercase letter, and one number'
                                    }
                                    label="New Password"
                                    placeholder="New Password"
                                    size="small"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={
                                                        handleClickShowPassword
                                                    }
                                                    onMouseDown={
                                                        handleMouseDownPassword
                                                    }
                                                    edge="end"
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        </Field>
                        <Field name="confirmPassword">
                            {({ field }: FieldProps<string>) => (
                                <StyledTextInput
                                    {...field}
                                    InputLabelProps={{ shrink: true }}
                                    id="confirmPassword"
                                    type={
                                        showPasswordConfirmation
                                            ? 'text'
                                            : 'password'
                                    }
                                    error={
                                        !!errors.confirmPassword &&
                                        touched.confirmPassword
                                    }
                                    helperText={
                                        !!errors.confirmPassword &&
                                        touched.confirmPassword &&
                                        errors.confirmPassword
                                    }
                                    label="Re-Enter Password"
                                    placeholder="Re-Enter Password"
                                    size="small"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={
                                                        handleClickShowPasswordConfirmation
                                                    }
                                                    onMouseDown={
                                                        handleMouseDownPassword
                                                    }
                                                    edge="end"
                                                >
                                                    {showPasswordConfirmation ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        </Field>
                        <StyledButtonContainer>
                            <Button type="submit" disabled={isSubmitting}>
                                Reset Password
                            </Button>
                        </StyledButtonContainer>
                    </Form>
                )}
            </Formik>
        </SignUpSignIn>
    )
}

export default ResetPasswordForm
